/**
 * Shared low-capacity playback policy for player queue filters, VBuffer gates,
 * Studio inventory warnings, and registration-status key contracts.
 *
 * @see docs/tenant-settings.md (`playerCreativeCrossfadeOnLowCapacity`, `playerAllowVideoOnLowCapacity`)
 */

/** Capacity tier string as reported on hardware snapshots / player state (`low` | `mid` | `high`). */
export type TPlayerCapacityTier = string | null | undefined

/**
 * Tenant keys that must appear on registration-status `tenant` for low-capacity policy.
 * Partial deploys that omit these leave players on stale defaults.
 */
export const PLAYER_LOW_CAPACITY_REGISTRATION_TENANT_KEYS = [
  'playerCreativeCrossfadeOnLowCapacity',
  'playerAllowVideoOnLowCapacity'
] as const

export type TPlayerLowCapacityRegistrationTenantKey
  = (typeof PLAYER_LOW_CAPACITY_REGISTRATION_TENANT_KEYS)[number]

/**
 * True when the device is known low capacity and the tenant has not opted the feature in.
 * `null`/unknown/`mid`/`high` never force off.
 */
export function isLowCapacityFeatureForcedOff(
  capacityTier: TPlayerCapacityTier,
  allowOnLowCapacity: boolean | null | undefined
): boolean {
  return capacityTier === 'low' && allowOnLowCapacity !== true
}

/** Video may play at this capacity (mid/high/unknown always; low only when opted in). */
export function isVideoPlaybackAllowedOnCapacity(
  capacityTier: TPlayerCapacityTier,
  allowVideoOnLowCapacity: boolean | null | undefined
): boolean {
  return !isLowCapacityFeatureForcedOff(capacityTier, allowVideoOnLowCapacity)
}

/**
 * Whether `resolvePlaybackSegments` should set `excludeFramesWithVideo`.
 * Unregistered players never exclude (no tenant settings).
 */
export function shouldExcludeFramesWithVideoOnCapacity(
  capacityTier: TPlayerCapacityTier,
  allowVideoOnLowCapacity: boolean | null | undefined,
  registered: boolean
): boolean {
  if (!registered) {
    return false
  }
  return isLowCapacityFeatureForcedOff(capacityTier, allowVideoOnLowCapacity)
}

/**
 * Studio inventory amber warning: low tier and allow-video is off.
 * Policy-based — does not inspect the live playlist.
 */
export function showsLowCapacityVideoSkipWarning(
  capacityTier: TPlayerCapacityTier,
  allowVideoOnLowCapacity: boolean | null | undefined
): boolean {
  return isLowCapacityFeatureForcedOff(capacityTier, allowVideoOnLowCapacity)
}
